import server from 'api';

// Booking changes on a coupon-first booking (gastronaut-api
// Coupons/bookingChangeRoute.ts): preview before saving, confirm after.

export type CouponCalculation = {
  guests: number;
  grossAmount: number;
  discount: number;
  netAmount: number;
  partialPayment: number;
  onlineDue: number;
  later: number;
};

export type CouponChangeSummary = {
  applies: boolean;
  code?: string;
  previous?: { guests: number; date: string; occassion: string };
  next?: { guests: number; date: string; time: string; occassion: string; grossAmount: number };
  evaluation?: {
    before: CouponCalculation;
    after: CouponCalculation;
    unmetConditions: string[];
    appliesWithoutGoodwill: boolean;
    goodwillApplied: boolean;
    paidAmount: number;
    openAmount: number;
    financialChange: boolean;
  };
};

export type CouponChangeDecision = {
  decision: 'goodwill' | 'withdraw' | null;
  note: string;
  summary: CouponChangeSummary;
};

/** Fields left out are read from the saved booking. */
export type ProposedBookingChange = {
  guests?: number;
  date?: string;
  time?: string;
  occassion?: string;
  ticket?: { options?: Record<string, number>; optionalAddOns?: Record<string, number> };
};

const path = (restaurantId: string, reservationId: string) =>
  `/v03/reservations/${restaurantId}/res/${reservationId}/couponChange`;

export const previewCouponChange = (
  restaurantId: string,
  reservationId: string,
  next: ProposedBookingChange,
  decision?: 'goodwill',
): Promise<CouponChangeSummary> =>
  server.post(path(restaurantId, reservationId), { mode: 'preview', next, decision }).then((r: any) => r.data);

export const confirmCouponChange = (
  restaurantId: string,
  reservationId: string,
  decision: CouponChangeDecision,
): Promise<CouponChangeSummary & { recorded?: boolean; emailed?: boolean }> =>
  server
    .post(path(restaurantId, reservationId), {
      mode: 'confirm',
      ...(decision.decision ? { decision: decision.decision } : {}),
      note: decision.note || undefined,
      expected: decision.summary.evaluation
        ? { discount: decision.summary.evaluation.after.discount, openAmount: decision.summary.evaluation.openAmount }
        : undefined,
    })
    .then((r: any) => r.data);

// The dialog lives once in the app shell (RestaurantContext, next to Alert);
// a save flow asks it for a decision and waits.
type DialogRequest = {
  withoutGoodwill: CouponChangeSummary;
  withGoodwill: CouponChangeSummary | null;
  resolve: (decision: CouponChangeDecision | null) => void;
};

let listener: ((request: DialogRequest) => void) | null = null;

export const registerCouponChangeDialog = (handler: ((request: DialogRequest) => void) | null) => {
  listener = handler;
};

export type { DialogRequest as CouponChangeDialogRequest };

// The dialog renders all three parts; an incomplete answer aborts the save.
const assertComplete = (summary: CouponChangeSummary) => {
  if (!summary.evaluation || !summary.previous || !summary.next) {
    throw new Error('Incomplete coupon comparison.');
  }
};

/**
 * Null when the booking holds no coupon promise. Throws when the comparison
 * cannot be loaded; the caller must not save then. Resolves 'cancelled' when
 * staff step back.
 */
export const reviewCouponChange = async (
  restaurantId: string,
  reservationId: string,
  next: ProposedBookingChange,
): Promise<CouponChangeDecision | 'cancelled' | null> => {
  const withoutGoodwill = await previewCouponChange(restaurantId, reservationId, next);
  if (!withoutGoodwill.applies) return null;
  assertComplete(withoutGoodwill);

  const evaluation = withoutGoodwill.evaluation!;
  if (!evaluation.financialChange && !evaluation.unmetConditions.length) {
    return { decision: null, note: '', summary: withoutGoodwill };
  }

  const withGoodwill = evaluation.unmetConditions.length
    ? await previewCouponChange(restaurantId, reservationId, next, 'goodwill')
    : null;
  if (withGoodwill) assertComplete(withGoodwill);

  if (!listener) throw new Error('Coupon change dialog is not mounted.');
  const decision = await new Promise<CouponChangeDecision | null>((resolve) =>
    listener!({ withoutGoodwill, withGoodwill, resolve })
  );
  return decision ?? 'cancelled';
};
